'use client';
import { useState } from 'react';
import { Link } from '@/i18n/navigation';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslations } from 'next-intl';
import OAuthButtons from '@/components/auth/OAuthButtons';
import AuthPageIcon from '@/components/auth/AuthPageIcon';

export default function LoginPage() {
  const router = useRouter();
  const t = useTranslations('auth');
  const tCommon = useTranslations('common');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  // Determine current locale from URL prefix (fr or ar)
  const locale = typeof window !== 'undefined'
    ? window.location.pathname.match(/^\/(fr|ar)/)?.[1] || 'fr'
    : 'fr';

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        // Special handling: redirect to OTP verification if account is pending
        if (data.code === 'PENDING_OTP' && data.email) {
          toast.error(data.error || t('invalidCredentials'));
          router.push(`/${locale}/verifier?email=${encodeURIComponent(data.email)}`);
          return;
        }
        if (data.code === 'PENDING_APPROVAL') {
          toast.error(data.error || t('invalidCredentials'));
          // /en-attente, /admin, /enseignant, /mon-compte are at ROOT level (no [locale] prefix)
          router.push(`/en-attente`);
          return;
        }
        toast.error(data.error || t('invalidCredentials'));
        return;
      }
      toast.success('Bienvenue ! 🎉');
      // /admin, /enseignant, /mon-compte are at ROOT level (no [locale] prefix)
      if (data.user.role === 'ADMIN') router.push(`/admin`);
      else if (data.user.role === 'TEACHER') router.push(`/enseignant`);
      else router.push(`/mon-compte`);
    } catch {
      toast.error(tCommon('error'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-sky-50 flex items-start justify-center p-4 pt-24">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link href="/" className="inline-flex items-center gap-2 mb-6">
            <AuthPageIcon />
          </Link>
          <h1 className="text-2xl font-extrabold">{t('loginTitle')}</h1>
          <p className="text-slate-500 mt-1">{t('loginSubtitle')}</p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-2xl p-6 lg:p-8 shadow-xl border border-slate-100"
        >
          <div className="mb-4">
            <label className="label">{t('email')}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="input"
              placeholder="votre@email.com"
            />
          </div>
          <div className="mb-6">
            <label className="label">{t('password')}</label>
            <div className="relative">
              <input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="input pr-10"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full justify-center py-3 text-base"
          >
            {loading ? '...' : t('loginButton')}
          </button>

          <OAuthButtons />

          <div className="text-center mt-4">
            <Link href="/mot-de-passe-oublie" className="text-sm text-primary-600 hover:underline">
              {t('forgotPassword')}
            </Link>
          </div>
        </form>

        <p className="text-center mt-6 text-sm text-slate-500">
          {t('noAccount')}{' '}
          <Link href="/inscription" className="text-primary-600 font-semibold hover:underline">
            {t('signupNow')}
          </Link>
        </p>
      </div>
    </div>
  );
}
